import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import { ScrollView, StyleSheet } from 'react-native';
import { SettingsItemNavigate } from 'design-system/SettingsItem';
import { SettingsSection } from 'design-system/SettingsSection';
import { AppView } from 'design-system/AppView';
import { rem } from 'design-system/values';
import * as Icons from 'design-system/icons';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { formatAmount } from 'utils';
import NavigationHeaderRightIconButton from 'design-system/NavigationHeaderRightIconButton';
import useColors from 'hooks/useColors';
import Modal from 'utils/packages/Modal';
import Text from 'design-system/Text';
import { Sign } from 'utils/types/navigation';
import { format, parseISO } from 'date-fns';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { renameTransaction } from 'actions';
import { isWeb } from '../../../constants';
import { makeSelectTransactionById } from '../selectors';
import useValuationChange from '../hooks/useValuationChange';
const strings = {
    headerTitle: 'Valuation change',
    reference: 'Reference',
    date: 'Date',
    frequency: 'Frequency',
    amount: 'Amount',
    category: 'Category',
    createdTransactions: 'Created transactions',
    modal: {
        saveChanges: {
            title: 'Save valuation change',
            subtitle: 'This will update the valuation of the asset',
            confirmButtonTitle: 'Got it, let me save',
            cancelButtonTitle: 'Cancel',
        },
        deleteTransaction: {
            title: 'Delete valuation change',
            subtitle: 'Are you sure you want to delete this valuation change?',
            confirmButtonTitle: 'Delete',
            cancelButtonTitle: 'Cancel',
        },
    },
    amountChangeText: 'What’s the amount for this valuation change?',
};
const EditValuationChangeContent = ({ transaction, navigation, onScroll, route, }) => {
    const colors = useColors();
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const { accountType } = route.params;
    const { deleteValuationChange, editValuationChange, editValuationDate } = useValuationChange();
    const dispatch = useAppDispatch();
    const nameToDisplay = transaction.customName || transaction.counterpartName;
    const dateToDisplay = transaction.customDate || transaction.bookingDate;
    const onCommitDelete = useCallback(async () => {
        deleteValuationChange(transaction.id, accountType);
    }, [accountType, deleteValuationChange, transaction.id]);
    const onPressDelete = useCallback(() => {
        Modal.showConfirmation({
            title: strings.modal.deleteTransaction.title,
            subtitle: strings.modal.deleteTransaction.subtitle,
            confirmButtonTitle: strings.modal.deleteTransaction.cancelButtonTitle,
            cancelButtonTitle: strings.modal.deleteTransaction.confirmButtonTitle,
            onPressCancel: onCommitDelete,
        });
    }, [onCommitDelete]);
    const onEditAmount = useCallback(() => {
        navigation.navigateDeprecated('AddBalance', {
            custom: {
                title: strings.amountChangeText,
                button: 'Save',
                icon: (<Icons.IconCoins height={32} width={32} alt2 gradientKey="darkPink"/>),
                hideCurrencyPicker: true,
                defaultValue: transaction.amount < 0 ? transaction.amount * -1 : transaction.amount,
                defaultCurrency: transaction.currency,
                initialSign: transaction.amount > 0 ? Sign.Positive : Sign.Negative,
                webSubtitle: strings.amountChangeText,
                webBottomSignSwap: true,
            },
            onContinue: (amount) => {
                editValuationChange(transaction.id, accountType, amount, transaction.amount);
                navigation.goBack();
            },
        });
    }, [
        accountType,
        editValuationChange,
        transaction.amount,
        transaction.currency,
        transaction.id,
    ]);
    const onEditDate = useCallback(() => {
        navigation.navigateDeprecated('SelectDate', {
            buttonText: 'Save',
            onSubmitDate: (date) => {
                editValuationDate(transaction, date, dateToDisplay, accountType);
                navigation.goBack();
            },
            props: {
                initialDate: dateToDisplay,
            },
        });
    }, [accountType, dateToDisplay, editValuationDate, transaction]);
    const onEditName = useCallback(() => {
        navigation.navigateDeprecated('TextFieldPage', {
            title: strings.reference,
            defaultValue: nameToDisplay,
            buttonTitle: 'Save',
            onPressNext: async (value) => {
                dispatch(renameTransaction([transaction.id.toString()], value, nameToDisplay));
                navigation.goBack();
            },
        });
    }, [nameToDisplay, transaction.id]);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => (<NavigationHeaderRightIconButton onPress={onPressDelete}>
          <Icons.IconTrash color={colors.buttons.primary}/>
        </NavigationHeaderRightIconButton>),
        });
    }, [colors.buttons.primary, onPressDelete]);
    return (<AppView withFrame={false}>
      <ScrollView onScroll={onScroll} style={paddingHorizontalStyle} scrollEventThrottle={16}>
        <SettingsSection noMarginTop style={styles.marginTop}>
          <SettingsItemNavigate title={nameToDisplay} description={strings.reference} onPress={onEditName} icon={<Icons.IconText gradientKey="lightPurple"/>}/>
          <SettingsItemNavigate title={format(parseISO(dateToDisplay), 'dd MMM yyyy')} description={strings.date} onPress={onEditDate} icon={<Icons.IconBoldCalendar gradientKey="darkPurple"/>}/>
          <SettingsItemNavigate title={<Text Numbers-16>
                {formatAmount(transaction.amount, transaction.currency, 2)}
              </Text>} description={strings.amount} onPress={onEditAmount} icon={<Icons.IconCoins alt gradientKey="darkPink"/>}/>
        </SettingsSection>
      </ScrollView>
    </AppView>);
};
const EditValuationChangeScreen = withAnimatedNavigationHeader(({ navigation, onScroll, route }) => {
    const { transactionId } = route.params;
    const memoSelectTransactionById = useMemo(makeSelectTransactionById, []);
    const transaction = useAppSelector((store) => memoSelectTransactionById(store, transactionId));
    if (!transaction) {
        navigation.goBack();
        return null;
    }
    return (<EditValuationChangeContent transaction={transaction} navigation={navigation} onScroll={onScroll} route={route}/>);
}, ({ colors }) => ({
    title: strings.headerTitle,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styles = StyleSheet.create({
    marginTop: {
        marginTop: rem(16),
    },
});
export default EditValuationChangeScreen;
